import { useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  LayoutDashboard,
  Building2,
  ToggleLeft,
  Ticket,
  ScrollText,
  ShieldCheck,
  Database,
  LogOut,
  RefreshCw,
  ChevronRight,
  CreditCard,
  SlidersHorizontal,
  ExternalLink,
  Cog,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminAuthStore } from '../store/authStore';
import { ADMIN_ROUTES } from '../router/routes';

const SIDEBAR_WIDTH = 240;

const NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, route: ADMIN_ROUTES.DASHBOARD },
  { label: 'Institutes', icon: Building2, route: ADMIN_ROUTES.INSTITUTES },
  { label: 'Subscription Plans', icon: CreditCard, route: ADMIN_ROUTES.PLANS },
  { label: 'Feature Flags', icon: ToggleLeft, route: ADMIN_ROUTES.FEATURE_FLAGS },
  { label: 'System Config', icon: SlidersHorizontal, route: ADMIN_ROUTES.SYSTEM_CONFIG },
  { label: 'Support Tickets', icon: Ticket, route: ADMIN_ROUTES.SUPPORT_TICKETS },
  { label: 'Audit Logs', icon: ScrollText, route: ADMIN_ROUTES.AUDIT_LOGS },
  { label: 'Roles & Permissions', icon: ShieldCheck, route: ADMIN_ROUTES.ROLES },
  { label: 'Cache Management', icon: Database, route: ADMIN_ROUTES.CACHE },
];

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/institutes': 'Institute Management',
  '/plans': 'Subscription Plans',
  '/feature-flags': 'Feature Flags & Product Settings',
  '/system-config': 'System Configurations',
  '/support-tickets': 'Support Tickets',
  '/audit-logs': 'Audit Logs',
  '/roles': 'Roles & Permissions',
  '/cache': 'Cache Management',
};

function resolvePageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (/^\/support-tickets\/\d+/.test(pathname)) return 'Ticket Detail';
  return 'Admin';
}

export function AdminAppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, logout } = useAdminAuthStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const user = session?.user;
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : 'SA';
  const pageTitle = resolvePageTitle(location.pathname);

  const appEnv = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE ?? 'development';
  const isProduction = appEnv === 'production';
  const isDevEnv = appEnv === 'development';
  const ENV_BANNER_HEIGHT = 28;

  const handleOpenHangfire = useCallback(() => {
    const apiBase = (import.meta.env.VITE_API_URL as string ?? '').replace(/\/api\/?$/, '');
    const token = session?.accessToken ?? '';
    // Set cookie via a pre-flight fetch so all /hangfire asset sub-requests are authenticated,
    // then open the dashboard. The backend filter reads "HangfireAccess" cookie directly.
    fetch(`${apiBase}/hangfire-set-cookie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'include',
    }).finally(() => {
      window.open(`${apiBase}/hangfire`, '_blank', 'noopener,noreferrer');
    });
  }, [session?.accessToken]);

  const handleRefresh = useCallback(() => {
    setSpinning(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setSpinning(false), 600);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Full-width environment banner */}
      {!isProduction && (
        <Box
          sx={{
            width: '100%',
            height: `${ENV_BANNER_HEIGHT}px`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 200,
            background: isDevEnv
              ? 'linear-gradient(90deg, #064e3b 0%, #065f46 40%, #047857 60%, #064e3b 100%)'
              : 'linear-gradient(90deg, #78350f 0%, #92400e 40%, #b45309 60%, #78350f 100%)',
            backgroundSize: '200% 100%',
            animation: 'envBannerShimmer 4s linear infinite',
            '@keyframes envBannerShimmer': {
              '0%': { backgroundPosition: '0% 0%' },
              '100%': { backgroundPosition: '200% 0%' },
            },
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isDevEnv ? '#6ee7b7' : '#fcd34d',
              animation: 'envDotPulse 2s ease-out infinite',
              '@keyframes envDotPulse': {
                '0%': { boxShadow: isDevEnv ? '0 0 0 0 rgba(110,231,183,0.7)' : '0 0 0 0 rgba(252,211,77,0.7)' },
                '70%': { boxShadow: isDevEnv ? '0 0 0 6px rgba(110,231,183,0)' : '0 0 0 6px rgba(252,211,77,0)' },
                '100%': { boxShadow: isDevEnv ? '0 0 0 0 rgba(110,231,183,0)' : '0 0 0 0 rgba(252,211,77,0)' },
              },
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: isDevEnv ? '#d1fae5' : '#fef3c7',
              fontFamily: 'monospace',
              userSelect: 'none',
            }}
          >
            {isDevEnv ? 'Development' : 'Staging'} Environment
          </Box>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: 3,
                borderRadius: '50%',
                backgroundColor: isDevEnv ? 'rgba(110,231,183,0.35)' : 'rgba(252,211,77,0.35)',
                mx: 0.25,
              }}
            />
          ))}
          <Box
            component="span"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 500,
              letterSpacing: '0.05em',
              color: isDevEnv ? 'rgba(209,250,229,0.6)' : 'rgba(254,243,199,0.6)',
              fontFamily: 'monospace',
              userSelect: 'none',
            }}
          >
            Not for production use
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: '100vh', mt: !isProduction ? `${ENV_BANNER_HEIGHT}px` : 0 }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: 'admin.sidebar',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: !isProduction ? `${ENV_BANNER_HEIGHT}px` : 0,
          left: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        {/* Brand */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #8A70D6 0%, #6A50B6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  color: '#fff',
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                SM
              </Box>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.75rem',
                  display: 'block',
                  lineHeight: 1.2,
                }}
              >
                SkolarMinds
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Admin Console
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation */}
        <List sx={{ pt: 1.5, px: 0, flex: 1 }}>
          {NAV_ITEMS.map(({ label, icon: Icon, route }) => {
            const isActive = route === '/'
            ? location.pathname === route
            : location.pathname === route || location.pathname.startsWith(route + '/');
            return (
              <ListItemButton
                key={route}
                selected={isActive}
                onClick={() => navigate(route)}
                sx={{
                  mx: 1,
                  mb: 0.25,
                  borderRadius: '7px',
                  ...(isActive && {
                    borderLeft: '3px solid #8A70D6',
                    borderRadius: '0 7px 7px 0',
                    ml: 0,
                    pl: '13px',
                  }),
                }}
              >
                <ListItemIcon>
                  <Icon size={16} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  slotProps={{
                    primary: {
                      sx: { fontSize: '0.8125rem', fontWeight: isActive ? 700 : 500 },
                    },
                  }}
                />
                {isActive && (
                  <ChevronRight size={13} style={{ color: '#8A70D6', opacity: 0.6 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Divider />

        {/* Hangfire dashboard link */}
        <List sx={{ py: 0.5, px: 0 }}>
          <Tooltip title="Opens Hangfire dashboard in a new tab (SystemAdmin only)" placement="right">
            <ListItemButton
              onClick={handleOpenHangfire}
              sx={{
                mx: 1,
                mb: 0.25,
                borderRadius: '7px',
                color: 'text.secondary',
                py: '6px',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Cog size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Hangfire Jobs"
                slotProps={{ primary: { sx: { fontSize: '0.8125rem', fontWeight: 500 } } }}
              />
              <ExternalLink size={12} style={{ opacity: 0.4 }} />
            </ListItemButton>
          </Tooltip>
        </List>

        <Divider />

        {/* User footer */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: 'primary.dark',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3,
              }}
            >
              {user ? `${user.firstName} ${user.lastName}` : 'System Admin'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.6rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.email ?? 'SystemAdmin'}
            </Typography>
          </Box>
          <Tooltip title="Sign out" placement="top">
            <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
              <LogOut size={15} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          ml: `${SIDEBAR_WIDTH}px`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Topbar */}
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 90,
            px: 3,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'admin.topbar',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {pageTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{
                  color: spinning ? 'primary.main' : 'text.secondary',
                  '& svg': {
                    transition: 'transform 600ms ease',
                    transform: spinning ? 'rotate(360deg)' : 'rotate(0deg)',
                  },
                }}
              >
                <RefreshCw size={15} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Page content */}
        <Box sx={{ flex: 1, p: 3 }} key={refreshKey}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{ height: '100%' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
      </Box>
    </Box>
  );
}
